import { forwardRef, useId, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { token, type AutomationLevel } from '../tokens';
import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './ProgressBar.module.css';

export interface ProgressBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Visible label. Also the accessible name of the progressbar. */
  label: ReactNode;
  /** 0..max. Omit for an indeterminate bar. */
  value?: number;
  max?: number;
  /** Overrides the printed value, e.g. "6 of 9 gates". */
  valueText?: string;
  /** Print the percentage (or `valueText`) at the end of the label row. */
  showValue?: boolean;
  helperText?: ReactNode;
  /**
   * Automation mix meters colour by level. Default is the interactive blue,
   * which is what a plain "3 of 9 complete" bar wants.
   */
  tone?: AutomationLevel | 'default';
  size?: 'sm' | 'md';
}

const TONE = {
  default: token.interactive.base,
  automated: token.domain.automation.automated,
  assisted: token.domain.automation.assisted,
  manual: token.domain.automation.manual,
} as const;

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(function ProgressBar(
  {
    label,
    value,
    max = 100,
    valueText,
    showValue = false,
    helperText,
    tone = 'default',
    size = 'md',
    className,
    style,
    ...rest
  },
  ref,
) {
  const labelId = useId();
  const indeterminate = value === undefined;
  const safeMax = max <= 0 ? 1 : max;
  const clamped = indeterminate ? 0 : Math.min(Math.max(value, 0), safeMax);
  const ratio = clamped / safeMax;
  const printed = valueText ?? `${String(Math.round(ratio * 100))}%`;

  const vars: StyleWithVars = {
    '--dpem-progress-fill': TONE[tone],
    '--dpem-progress-ratio': ratio,
  };

  return (
    <div
      {...rest}
      ref={ref}
      className={clsx(styles.wrapper, size === 'sm' && styles.sm, className)}
      style={mergeStyle(vars, style)}
    >
      <div className={styles.labelRow}>
        <span className={styles.label} id={labelId}>
          {label}
        </span>
        {showValue && !indeterminate && <span className={styles.value}>{printed}</span>}
      </div>
      <div
        className={clsx(styles.track, indeterminate && styles.indeterminate)}
        role="progressbar"
        aria-valuemin={indeterminate ? undefined : 0}
        aria-valuemax={indeterminate ? undefined : safeMax}
        aria-valuenow={indeterminate ? undefined : clamped}
        aria-valuetext={indeterminate ? undefined : printed}
        aria-labelledby={labelId}
      >
        <div className={styles.fill} />
      </div>
      {helperText !== undefined && <p className={styles.helper}>{helperText}</p>}
    </div>
  );
});
